import styled from '@emotion/styled';
import { alpha } from '@mui/material/styles';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';

import { changedField, fieldBorder, focusRing, hoverable } from '../../interaction-states';
import { forMobile } from '../../responsive';
import { scrollbarStyles } from '../../scrollbar';
import { typographyVariantStyles } from '../../typography-variants';

// ─── Trigger (input-like button) ─────────────────────────────────────────────

export const StyledTrigger = styled('div', {
  shouldForwardProp: (prop) =>
    !['$fullWidth', '$open', '$disabled', '$error'].includes(prop as string),
})<{ $fullWidth?: boolean; $open?: boolean; $disabled?: boolean; $error?: boolean }>(
  ({ theme, $fullWidth, $open, $disabled, $error }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    height: 40,
    backgroundColor: theme.colors.background.overlay,
    border: `1px solid ${fieldBorder(
      $error ? theme.colors.error.border : theme.colors.border.default,
    )}`,
    borderRadius: theme.radius[12],
    cursor: $disabled ? 'not-allowed' : 'pointer',
    width: $fullWidth ? '100%' : 'fit-content',
    boxSizing: 'border-box',
    userSelect: 'none',
    outline: 'none',

    ...changedField(theme),
    ...(!$error && hoverable(theme, 'border')),
    ...focusRing(theme),

    ...(!$disabled && !$error && $open && { borderColor: theme.colors.border.brand }),

    ...($disabled && {
      opacity: 0.5,
      pointerEvents: 'none',
    }),
  }),
);

export const StyledTriggerIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 16,
  height: 16,
  color: theme.colors.icon.primary,

  '& svg': {
    width: '100%',
    height: '100%',
  },
}));

export const StyledTriggerText = styled('span')(({ theme }) => ({
  flex: 1,
  ...typographyVariantStyles.body2,
  color: theme.colors.text.primary,
  whiteSpace: 'nowrap',

  '&[data-placeholder]': {
    color: theme.colors.text.secondary,
  },
}));

// ─── Calendar popup container ─────────────────────────────────────────────────

// 7 day columns × 48px + 6 × 1px gaps — the day grid's intrinsic width, kept
// consistent across the grid and Set Date views so the popper doesn't resize
// when switching between them.
const CALENDAR_CONTENT_WIDTH = 342;

export const StyledCalendarScrollArea = styled('div')(({ theme }) => ({
  maxHeight: 'calc(100dvh - 16px)',
  overflowY: 'auto',
  overflowX: 'hidden',
  borderRadius: theme.radius[16],
  ...scrollbarStyles,
}));

export const StyledCalendarContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: 16,
  width: CALENDAR_CONTENT_WIDTH,
  backgroundColor: theme.colors.background.raised,
  borderRadius: theme.radius[16],
  boxSizing: 'content-box',

  [forMobile]: {
    width: 'calc(100vw - 16px)',
    boxSizing: 'border-box',
    alignItems: 'center',
  },
}));

// Figma's range frame is 732px wide: two 342px day grids + this gap + the
// container's own 16px padding on each side.
const RANGE_GAP = 16;

export const StyledRangeContainer = styled(StyledCalendarContainer)({
  width: CALENDAR_CONTENT_WIDTH * 2 + RANGE_GAP,

  [forMobile]: {
    width: 'calc(100vw - 16px)',
  },
});

export const StyledRangeCalendars = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: RANGE_GAP,

  [forMobile]: {
    flexDirection: 'column',
  },
});

export const StyledRangeColumn = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

// ─── Footer (Cancel / Confirm) ────────────────────────────────────────────────

export const StyledFooter = styled('div')({
  display: 'flex',
  alignSelf: 'stretch',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 8,
});

// ─── DateCalendar overrides ───────────────────────────────────────────────────

// 6 rows × 48px cell + 5 gaps × 1px = 293px
const SLIDE_TRANSITION_MIN_HEIGHT = 293;

export const StyledDateCalendar = styled(DateCalendar)(({ theme }) => ({
  // Container reset — override MUI X defaults (maxHeight: 280, width: 320)
  width: 'auto',
  height: 392,
  maxHeight: 'none !important',
  padding: 0,

  // ── Weekday name row ────────────────────────────────────────────────────────

  '& .MuiDayCalendar-header': {
    gap: 1,
    marginBottom: 1,
  },

  '& .MuiDayCalendar-weekDayLabel': {
    width: 48,
    height: 48,
    margin: 0,
    borderRadius: 4,
    ...typographyVariantStyles.body3,
    fontWeight: 500,
    color: theme.colors.text.secondary,
  },

  // ── Week rows ───────────────────────────────────────────────────────────────

  '& .MuiDayCalendar-monthContainer': {
    position: 'relative',
  },

  // The slide transition children are position:absolute, so the parent needs an
  // explicit min-height that fits 6 weeks (the max any month can need).
  '& .MuiDayCalendar-slideTransition': {
    minHeight: SLIDE_TRANSITION_MIN_HEIGHT,
  },

  '& .MuiDayCalendar-weekContainer': {
    gap: 1,
    margin: 0,
    marginBottom: 1,

    '&:last-of-type': {
      marginBottom: 0,
    },
  },

  // ── Day cells ───────────────────────────────────────────────────────────────

  '& .MuiPickerDay-root': {
    width: 48,
    height: 48,
    margin: 0,
    borderRadius: theme.radius[8],
    backgroundColor: 'transparent',
    color: theme.colors.text.primary,
    ...typographyVariantStyles.body3,
    fontWeight: 500,
    border: 'none',
    transition: 'background-color 0.15s ease',

    '&:hover:not(.Mui-disabled)': {
      backgroundColor: alpha(theme.colors.brand.solid, 0.25),
    },

    '&:focus:not(.Mui-selected)': {
      backgroundColor: alpha(theme.colors.brand.solid, 0.15),
    },

    // Today indicator — subtle green border, same background
    '&.MuiPickerDay-today:not(.Mui-selected)': {
      border: `1px solid ${theme.colors.brand.solid}`,
    },

    // Selected
    '&.Mui-selected': {
      backgroundColor: theme.colors.brand.solid,
      color: theme.colors.text.primary,
      fontWeight: 500,

      '&:hover': {
        backgroundColor: theme.colors.brand.solid,
      },

      '&:focus': {
        backgroundColor: theme.colors.brand.solid,
      },
    },

    // Days outside the current month
    '&.MuiPickerDay-dayOutsideMonth': {
      color: theme.colors.text.disabled,
    },

    // Disabled days
    '&.Mui-disabled': {
      color: theme.colors.text.disabled,
      opacity: 1,
    },

    // Range states come from a data attribute set in RangeDay, so single and
    // range modes share one day-cell style block.
    "&[data-range-state='in-range']": {
      backgroundColor: alpha(theme.colors.brand.solid, 0.15),
      borderRadius: 0,
    },

    "&[data-range-state='preview']": {
      backgroundColor: alpha(theme.colors.brand.solid, 0.08),
      borderRadius: 0,
    },

    "&[data-range-state='range-start'], &[data-range-state='range-end'], &[data-range-state='single-day']":
      {
        backgroundColor: theme.colors.brand.solid,
        color: theme.colors.text.primary,

        '&:hover': {
          backgroundColor: theme.colors.brand.solid,
        },
      },

    "&[data-range-state='range-start']": {
      borderRadius: `${theme.radius[8]}px 0 0 ${theme.radius[8]}px`,
    },

    "&[data-range-state='range-end']": {
      borderRadius: `0 ${theme.radius[8]}px ${theme.radius[8]}px 0`,
    },

    "&[data-range-state='single-day']": {
      borderRadius: theme.radius[8],
    },
  },
})) as typeof DateCalendar;
